import { useState } from 'react';
import { placeKnockoutBet } from '../api/matchesApi';
import { Match } from '../api/matchesApi';

interface UsePlaceMatchBetOptions {
  onSuccess?: (matchId: string, match: Match) => void;
  onError?: (error: any) => void;
}

/**
 * Custom hook for placing match bets
 * Handles the API call and winnerId calculation
 * 
 * @param options - Optional callbacks for success/error handling
 * @returns Object with placeBet function and loading state
 */
export const usePlaceMatchBet = (options?: UsePlaceMatchBetOptions) => {
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});

  const placeBet = async (
    matchId: string,
    homeScore: string,
    awayScore: string,
    winnerId?: number | null,
    match?: Match
  ) => {
    const homeScoreNum = parseInt(homeScore, 10);
    const awayScoreNum = parseInt(awayScore, 10);

    // Validate scores
    if (isNaN(homeScoreNum) || isNaN(awayScoreNum) || homeScoreNum < 0 || awayScoreNum < 0) {
      const error = new Error('Invalid scores');
      options?.onError?.(error);
      return;
    }

    // Calculate winnerId if not provided
    let calculatedWinnerId: number | null | undefined = winnerId;
    
    if (calculatedWinnerId === undefined && match) {
      const isKnockout = match.round !== 'GROUP';
      
      if (isKnockout) {
        // For knockout matches, if it's a draw, winnerId should be the qualifier
        // Otherwise, it's determined by the score
        if (homeScoreNum === awayScoreNum) {
          // For draws in knockout, winnerId should be provided (qualifier)
          calculatedWinnerId = winnerId ?? null;
        } else if (homeScoreNum > awayScoreNum) {
          calculatedWinnerId = match.home_team_id ?? null;
        } else {
          calculatedWinnerId = match.away_team_id ?? null;
        }
      } else {
        // For group matches, winnerId is determined by the score (no draws need qualifier)
        if (homeScoreNum > awayScoreNum) {
          calculatedWinnerId = match.home_team_id ?? null;
        } else if (homeScoreNum < awayScoreNum) {
          calculatedWinnerId = match.away_team_id ?? null;
        } else {
          calculatedWinnerId = null; // Draw
        }
      }
    }

    try {
      setSubmitting(prev => ({ ...prev, [matchId]: true }));
      
      const result = await placeKnockoutBet(
        matchId,
        homeScoreNum,
        awayScoreNum,
        calculatedWinnerId ?? undefined
      );

      options?.onSuccess?.(matchId, result);
    } catch (error) {
      console.error(`Error placing bet on match ${matchId}:`, error);
      options?.onError?.(error);
      throw error;
    } finally {
      setSubmitting(prev => ({ ...prev, [matchId]: false }));
    }
  };

  return {
    placeBet,
    submitting,
    isSubmitting: (matchId: string) => submitting[matchId] ?? false,
  };
};

